import React, { useState } from 'react';
import {
  Box,
  Paper,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Tooltip,
  Chip,
  Badge,
  Fade,
  Alert,
  Snackbar,
  Stack,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  FormControl,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  DeviceHub as ChannelsIcon,
  Inventory as ItemsIcon,
  MoreVert as MoreIcon,
  Download as ExportIcon,
  Upload as ImportIcon,
  History as HistoryIcon,
  Store as StoreIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ViewList as ListViewIcon,
  GridView as GridViewIcon,
  Sort as SortIcon,
  FilterList as FilterIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  EventNote as SpecialHoursIcon,
} from '@mui/icons-material';
import { useStoreManagement, Store } from '../../context/StoreManagementContext';
import { HelpTooltip } from '../Onboarding';

type SortOption = 'name' | 'number' | 'status' | 'modified';

interface UnifiedActionBarProps {
  onBulkEdit?: (action: string, data?: any) => void;
  onExport?: () => void;
  onImport?: (file: File) => void;
  hasUnsavedChanges?: boolean;
  isApplying?: boolean;
  viewMode?: 'list' | 'grid';
  onViewModeChange?: (mode: 'list' | 'grid') => void;
  sortBy?: SortOption;
  onSortChange?: (sort: SortOption) => void;
  filteredStores: Store[];
  selectedStores: Store[];
}

export const UnifiedActionBar: React.FC<UnifiedActionBarProps> = ({
  onBulkEdit,
  onExport,
  onImport,
  hasUnsavedChanges = false,
  isApplying = false,
  viewMode = 'list',
  onViewModeChange,
  sortBy = 'name',
  onSortChange,
  filteredStores,
  selectedStores,
}) => {
  const {
    selectionCount,
    clearSelection,
    updateFilters,
    filters,
    clearFilters,
  } = useStoreManagement();

  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState(filters?.searchTerm || '');

  // Calculate active filter count
  const activeFilterCount = React.useMemo(() => {
    let count = 0;
    if (filters.onlineOrders !== undefined) count++;
    if (filters.status?.length > 0) count += filters.status.length;
    if (filters.channels?.delivery !== undefined) count++;
    if (filters.channels?.pickup !== undefined) count++;
    if (filters.channels?.dineIn !== undefined) count++;
    return count;
  }, [filters]);

  const handleMoreMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMoreMenuAnchor(event.currentTarget);
  };

  const handleMoreMenuClose = () => {
    setMoreMenuAnchor(null);
  };

  const handleAction = (action: string, data?: any) => {
    onBulkEdit?.(action, data);
    setSuccessMessage(`${selectionCount} stores selected for ${action.replace('bulk-edit-', '').replace('-', ' ')}`);
    setShowSuccessMessage(true);
  };

  const handleFilterOpen = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchor(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchor(null);
  };

  const handleStatusFilterToggle = (status: 'active' | 'inactive' | 'error') => {
    const newStatus = [...filters.status];
    const index = newStatus.indexOf(status);

    if (index > -1) {
      newStatus.splice(index, 1);
    } else {
      newStatus.push(status);
    }

    updateFilters({ status: newStatus });
  };

  return (
    <>
      <Fade in>
        <Paper
          elevation={0}
          sx={{
            px: { xs: 1, sm: 2, md: 2.5 },
            py: { xs: 0.75, sm: 1.25 },
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Unified Search Bar - Primary on Mobile */}
              <TextField
                size="small"
                placeholder="Search stores..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  updateFilters({ searchTerm: e.target.value });
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" sx={{ fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSearchQuery('');
                          updateFilters({ searchTerm: '' });
                        }}
                        edge="end"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.default',
                    borderRadius: 1,
                  }
                }}
              />

              <Badge badgeContent={activeFilterCount} color="primary">
                <IconButton 
                  onClick={handleFilterOpen}
                  color={activeFilterCount > 0 ? "primary" : "default"}
                  sx={{ 
                    border: 1, 
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1
                  }}
                >
                  <FilterIcon fontSize="small" />
                </IconButton>
              </Badge>

              <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
                <IconButton
                  size="small"
                  onClick={handleMoreMenuOpen}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}
                >
                  <MoreIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Desktop-only secondary row or Selection bar */}
            {(selectionCount > 0 || filteredStores.length > 0) && (
              <Box sx={{ 
                display: { xs: 'none', sm: 'flex' }, 
                alignItems: 'center', 
                justifyContent: 'space-between',
                pt: 0.5 
              }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {filteredStores.length} {filteredStores.length === 1 ? 'STORE' : 'STORES'}
                  </Typography>
                  {selectionCount > 0 && (
                    <Chip
                      label={`${selectionCount} selected`}
                      size="small"
                      color="secondary"
                      onDelete={clearSelection}
                    />
                  )}
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(e, newView) => newView && onViewModeChange?.(newView)}
                    size="small"
                  >
                    <ToggleButton value="list" sx={{ px: 1.5 }}><ListViewIcon fontSize="inherit" /></ToggleButton>
                    <ToggleButton value="grid" sx={{ px: 1.5 }}><GridViewIcon fontSize="inherit" /></ToggleButton>
                  </ToggleButtonGroup>
                  
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <Select
                      value={sortBy}
                      onChange={(e) => onSortChange?.(e.target.value as SortOption)}
                      displayEmpty
                      startAdornment={<SortIcon fontSize="small" sx={{ mr: 0.5, color: 'action.active' }} />}
                      sx={{ height: 32, fontSize: '0.8125rem' }}
                    >
                      <MenuItem value="name">Name A-Z</MenuItem>
                      <MenuItem value="number">Number</MenuItem>
                      <MenuItem value="status">Status</MenuItem>
                      <MenuItem value="modified">Last Modified</MenuItem>
                    </Select>
                  </FormControl>

                  <IconButton size="small" onClick={handleMoreMenuOpen}><MoreIcon /></IconButton>
                </Stack>
              </Box>
            )}
          </Stack>

          {/* Bulk Actions Bar - Shows when 2+ stores selected */}
          {selectionCount >= 2 && (
            <Box
              sx={{
                mt: 1.5,
                pt: 1.5,
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1, sm: 1.5 },
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Bulk Actions:
                </Typography>
                <HelpTooltip title="Apply changes to all selected stores at once. Select 2 or more stores using the checkboxes." iconSize={16} />
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<ScheduleIcon />}
                  onClick={() => handleAction('bulk-edit-hours')}
                  disabled={isApplying}
                  color="primary"
                >
                  Edit Hours ({selectionCount})
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SpecialHoursIcon />}
                  onClick={() => handleAction('bulk-edit-special-hours')}
                  disabled={isApplying}
                  color="info"
                >
                  Special Hours ({selectionCount})
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<ChannelsIcon />}
                  onClick={() => handleAction('bulk-edit-channels')}
                  disabled={isApplying}
                  color="secondary"
                >
                  Edit Channels ({selectionCount})
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<ItemsIcon />}
                  onClick={() => handleAction('bulk-edit-items')}
                  disabled={isApplying}
                  color="warning"
                >
                  Snooze Items ({selectionCount})
                </Button>
              </Stack>
            </Box>
          )}
        </Paper>
      </Fade>

      {/* Filter Popover */}
      <Popover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 320,
            maxWidth: 400,
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Filter Options
          </Typography>

          <Stack spacing={2}>
            {/* Online Orders Filter */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Online Orders
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={filters.onlineOrders === true}
                      onChange={() => updateFilters({ onlineOrders: filters.onlineOrders === true ? undefined : true })}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ActiveIcon color="success" fontSize="small" />
                      <Typography variant="body2">Online (Accepting Orders)</Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={filters.onlineOrders === false}
                      onChange={() => updateFilters({ onlineOrders: filters.onlineOrders === false ? undefined : false })}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <InactiveIcon color="disabled" fontSize="small" />
                      <Typography variant="body2">Offline (Not Accepting)</Typography>
                    </Box>
                  }
                />
              </FormGroup>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
              <Button size="small" onClick={clearFilters}>
                Clear All
              </Button>
              <Button size="small" variant="contained" onClick={handleFilterClose}>
                Apply
              </Button>
            </Box>
          </Stack>
        </Box>
      </Popover>

      {/* More Actions Menu */}
      <Menu
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={handleMoreMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => {
          handleAction('view-recent-changes');
          handleMoreMenuClose();
        }}>
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="View Activity Log" />
        </MenuItem>
      </Menu>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={3000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessMessage(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
};